import { Outlet, RouteObject } from "react-router-dom";
import {
  CONFIG_PAGES,
  PAGE_TITLES,
  PERMISSIONS,
  PLATFORM_MODULES,
} from "@miplatform/constants";
import {
  CalendarEventType,
  FeatureFlagKey,
  IWebPageRoute,
  PlatformModules,
} from "@miplatform/types";
import { MODULES_CONFIG_PATHS } from "./constants/configPaths";
import { ManageCannotPerformReasons } from "../../views/AdminSettings/ManageCannotPerformReasons/ManageCannotPerformReasons";
import { ManageLaborRates } from "../../views/AdminSettings/ManageLaborRates/ManageLaborRates";
import { ManageMiDaySettings } from "../../views/AdminSettings/ManageMiDaySettings/ManageMiDaySettings";
import { OrderAutoTrigger } from "../../views/Config/Auto/AutoTrigger/OrderAutoTrigger";
import { WorkOrderAutoTrigger } from "../../views/Config/Auto/AutoTrigger/WorkOrderAutoTrigger";
import { ConfigCalendarSetDetails } from "../../views/Config/Calendars/CalendarSetDetails/ConfigCalendarSetDetails";
import { ConfigCalendarSetList } from "../../views/Config/Calendars/ConfigCalendarSetList";
import { ConfigCityDetails } from "../../views/Config/Cities/CityDetails/ConfigCityDetails";
import { ConfigCitiesList } from "../../views/Config/Cities/ConfigCitiesList";
import { ConfigPageLayout } from "../../views/Config/ConfigPageLayout";
import { ConfigDepartmentList } from "../../views/Config/Departments/ConfigDepartmentList";
import { ConfigDepartmentDetails } from "../../views/Config/Departments/DepartmentDetails/ConfigDepartmentDetails";
import { ConfigDocumentTypesList } from "../../views/Config/DocumentTypes/ConfigDocumentTypesList";
import { ConfigHolidayList } from "../../views/Config/Holidays/ConfigHolidayList";
import { ConfigHolidayDetails } from "../../views/Config/Holidays/HolidayDetails/ConfigHolidayDetails";
import { ConfigInspectionAuthoritiesList } from "../../views/Config/InspectionAuthorities/ConfigInspectionAuthoritiesList";
import { ConfigInspectionAuthorityDetails } from "../../views/Config/InspectionAuthorities/InspectionAuthorityDetails/ConfigInspectionAuthorityDetails";
import { ConfigInspectionTemplateList } from "../../views/Config/InspectionTemplates/ConfigInspectionTemplateList";
import { ConfigInspectionTemplateDetails } from "../../views/Config/InspectionTemplates/InspectionTemplateDetails/ConfigInspectionTemplateDetails";
import { ConfigIssueTemplatesList } from "../../views/Config/IssueTemplates/ConfigIssueTemplatesList";
import { ConfigOrderTemplateList } from "../../views/Config/OrderTemplates/ConfigOrderTemplateList";
import { ConfigOrderTemplateDetails } from "../../views/Config/OrderTemplates/OrderTemplateDetails/ConfigOrderTemplateDetails";
import { ConfigQuickBooksImport } from "../../views/Config/QuickBooksImport/ConfigQuickBooksImport";
import { ConfigQuickBooksIntegration } from "../../views/Config/QuickBooksIntegration/ConfigQuickBooksIntegration";
import { ConfigRoleList } from "../../views/Config/Roles/ConfigRoleList";
import { ConfigRoleDetails } from "../../views/Config/Roles/RoleDetails/ConfigRoleDetails";
import { ConfigUserList } from "../../views/Config/Users/ConfigUserList";
import { ConfigUserDetails } from "../../views/Config/Users/UserDetails/ConfigUserDetails";
import { ConfigWorkOrderStepsList } from "../../views/Config/WorkOrderSteps/ConfigWorkOrderStepsList";
import { ConfigWorkOrderStepDetails } from "../../views/Config/WorkOrderSteps/WorkOrderStepDetails/ConfigWorkOrderStepDetails";
import { ConfigWorkOrderTemplatesList } from "../../views/Config/WorkOrderTemplates/ConfigWorkOrderTemplateList";
import { ConfigWorkOrderTemplateDetails } from "../../views/Config/WorkOrderTemplates/WorkOrderTemplateDetails/ConfigWorkOrderTemplateDetails";
import { CONFIG_PATHS, CONFIG_ROUTE_NAMES } from "./constants";

type WebPageRouteExtended = IWebPageRoute & RouteObject;

const getRouteData = (
  page: string,
  options?: {
    element?: React.ReactNode;
    childrenRoutes?: RouteObject[];
    handle?: { title: string };
    featureFlagKey?: FeatureFlagKey;
  }
): WebPageRouteExtended => {
  return {
    path: CONFIG_PATHS[page],
    exact: true,
    name: CONFIG_ROUTE_NAMES[page],
    element: options?.element || <ConfigPageLayout />,
    children: options?.childrenRoutes,
    handle: options?.handle,
    featureFlagKey: options?.featureFlagKey,
  };
};

const getChildRouteData = (
  path: string,
  name: string,
  options: {
    element?: React.ReactNode;
    groupName?: string;
    featureFlagKey?: FeatureFlagKey;
    handle?: { title: string };
    childrenRoutes?: RouteObject[];
  }
): WebPageRouteExtended => {
  return {
    path,
    exact: true,
    name,
    element: options?.element || <ConfigPageLayout />,
    handle: options?.handle,
    featureFlagKey: options?.featureFlagKey,
    children: options?.childrenRoutes,
  };
};

const ROLES_ELEMENTS = ({
  hidePermissions,
  hideWorkPermissions,
  modulePermission,
  module,
}: {
  hidePermissions?: boolean;
  hideWorkPermissions?: boolean;
  modulePermission?: string;
  module?: PlatformModules;
}) => {
  return {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigRoleList modulePermission={modulePermission} />,
        handle: { title: PAGE_TITLES.ROLES },
      },
      {
        path: ":id",
        element: (
          <ConfigRoleDetails
            hidePermissions={hidePermissions}
            hideWorkPermissions={hideWorkPermissions}
            module={module}
          />
        ),
        handle: { title: PAGE_TITLES.ROLE },
      },
      {
        path: "add-role",
        element: (
          <ConfigRoleDetails
            hidePermissions={hidePermissions}
            hideWorkPermissions={hideWorkPermissions}
            module={module}
          />
        ),
        handle: { title: PAGE_TITLES.ADD_ROLE },
      },
    ],
  };
};

const hrConfigChildRoutes = [
  getRouteData(CONFIG_PAGES.DEPARTMENTS, {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigDepartmentList />,
        handle: { title: PAGE_TITLES.DEPARTMENTS },
      },
      {
        path: ":id",
        element: <ConfigDepartmentDetails />,
        handle: { title: PAGE_TITLES.DEPARTMENT },
      },
      {
        path: "add-department",
        element: <ConfigDepartmentDetails />,
        handle: { title: PAGE_TITLES.ADD_DEPARTMENT },
      },
      {
        path: ":id/users/:userId",
        element: <ConfigUserDetails />,
        handle: { title: PAGE_TITLES.USERS },
      },
    ],
  }),
  getRouteData(CONFIG_PAGES.HOLIDAYS, {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigHolidayList />,
        handle: { title: PAGE_TITLES.HOLIDAYS },
      },
      {
        path: ":id",
        element: <ConfigHolidayDetails />,
        handle: { title: PAGE_TITLES.HOLIDAY },
      },
      {
        path: "add-holiday",
        element: <ConfigHolidayDetails />,
        handle: { title: PAGE_TITLES.ADD_HOLIDAY },
      },
    ],
  }),
  getRouteData(CONFIG_PAGES.USERS, {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigUserList />,
        handle: { title: PAGE_TITLES.USERS },
      },
      {
        path: ":id",
        element: <ConfigUserDetails />,
        handle: { title: PAGE_TITLES.USER },
      },
      {
        path: "add-user",
        element: <ConfigUserDetails />,
        handle: { title: PAGE_TITLES.ADD_USER },
      },
    ],
  }),
  getRouteData(
    CONFIG_PAGES.ROLES_HR,
    ROLES_ELEMENTS({ hidePermissions: true, hideWorkPermissions: true })
  ),
];

const operationsChildRoutes = [
  getRouteData(CONFIG_PAGES.ORDER_TEMPLATES, {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigOrderTemplateList />,
        handle: { title: PAGE_TITLES.ORDER_TEMPLATES },
      },
      {
        path: ":id",
        element: <ConfigOrderTemplateDetails />,
        handle: { title: PAGE_TITLES.ORDER_TEMPLATE },
      },
      {
        path: "add-order-type",
        element: <ConfigOrderTemplateDetails />,
        handle: { title: PAGE_TITLES.ADD_ORDER_TEMPLATE },
      },
    ],
  }),
  getRouteData(
    CONFIG_PAGES.ROLES_OPERATIONS,
    ROLES_ELEMENTS({ hideWorkPermissions: true })
  ),
  getRouteData(CONFIG_PAGES.RECORDABLES, {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigIssueTemplatesList view="recordables" />,
        handle: { title: PAGE_TITLES.RECORDABLES },
      },
    ],
  }),
  getRouteData(CONFIG_PAGES.WARRANTY_ITEMS, {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigIssueTemplatesList view="warrantyItems" />,
        handle: { title: PAGE_TITLES.WARRANTY_ITEMS },
      },
    ],
  }),
  getRouteData(CONFIG_PAGES.CAMERA_LINES, {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigIssueTemplatesList view="cameraLines" />,
        handle: { title: PAGE_TITLES.CAMERA_LINES },
      },
    ],
  }),
  getRouteData(CONFIG_PAGES.DOCUMENT_TYPES, {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigDocumentTypesList />,
      },
    ],
  }),
  getRouteData(CONFIG_PAGES.INSPECTION_AUTHORITIES, {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigInspectionAuthoritiesList />,
        handle: { title: PAGE_TITLES.INSPECTION_AUTHORITIES },
      },
      {
        path: ":id",
        element: <ConfigInspectionAuthorityDetails />,
        handle: { title: PAGE_TITLES.INSPECTION_AUTHORITY },
      },
      {
        path: "add-inspection-authority",
        element: <ConfigInspectionAuthorityDetails />,
        handle: { title: PAGE_TITLES.ADD_INSPECTION_AUTHORITY },
      },
    ],
  }),
  getRouteData(CONFIG_PAGES.CITIES, {
    element: <Outlet />,
    childrenRoutes: [
      {
        index: true,
        element: <ConfigCitiesList />,
        handle: { title: PAGE_TITLES.CITIES },
      },
      {
        path: ":id",
        element: <ConfigCityDetails />,
        handle: { title: PAGE_TITLES.CITY },
      },
      {
        path: "add-city",
        element: <ConfigCityDetails />,
        handle: { title: PAGE_TITLES.ADD_CITY },
      },
    ],
  }),
];

const autoChildRoutes = [
  getRouteData(CONFIG_PAGES.ORDER_AUTO_TRIGGER, {
    element: <OrderAutoTrigger />,
    childrenRoutes: [],
  }),
  getRouteData(CONFIG_PAGES.WORK_ORDER_AUTO_TRIGGER, {
    element: <WorkOrderAutoTrigger />,
    featureFlagKey: FeatureFlagKey.AdvancedWorkOrdersEnabled,
    childrenRoutes: [],
  }),
  getRouteData(CONFIG_PAGES.AUTO_BUILD, {
    element: <div>Auto Build</div>,
    featureFlagKey: FeatureFlagKey.AdvancedWorkOrdersEnabled,
  }),
  getRouteData(CONFIG_PAGES.AUTO_ASSIGN, {
    element: <div>Auto Assign</div>,
    featureFlagKey: FeatureFlagKey.AdvancedWorkOrdersEnabled,
  }),
];

const miDispatchRoutes: WebPageRouteExtended[] = [
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_DISPATCH][CONFIG_PAGES.USERS],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.USERS],
    {
      element: <ConfigUserList module={PERMISSIONS.MI_DISPATCH.ACCESS} />,
      handle: { title: PAGE_TITLES.USERS },
    }
  ),
  getChildRouteData(
    `${
      MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_DISPATCH][CONFIG_PAGES.USERS]
    }/:id`,
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_DISPATCH][CONFIG_PAGES.USERS],
    {
      element: <ConfigUserDetails />,
      handle: { title: PAGE_TITLES.USER },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_DISPATCH][
      CONFIG_PAGES.BUILD_CALENDAR
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.BUILD_CALENDAR],
    {
      element: <ConfigPageLayout />,
      handle: { title: PAGE_TITLES.BUILD_CALENDAR },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_DISPATCH][
      CONFIG_PAGES.CREATE_TEAM
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.CREATE_TEAM],
    {
      element: <ConfigPageLayout />,
      handle: { title: PAGE_TITLES.CREATE_TEAM },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_DISPATCH][
      CONFIG_PAGES.ORDER_CALENDARS
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.ORDER_CALENDARS],
    {
      element: <Outlet />,
      handle: { title: PAGE_TITLES.CALENDARS },
      childrenRoutes: [
        {
          index: true,
          element: (
            <ConfigCalendarSetList
              module={PlatformModules.MiDispatch}
              eventType={CalendarEventType.Order}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
        {
          path: ":id",
          element: (
            <ConfigCalendarSetDetails
              module={PlatformModules.MiDispatch}
              eventType={CalendarEventType.Order}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
        {
          path: "add-calendar",
          element: (
            <ConfigCalendarSetDetails
              module={PlatformModules.MiDispatch}
              eventType={CalendarEventType.Order}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
      ],
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_DISPATCH][
      CONFIG_PAGES.LABOR_RATES
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.LABOR_RATES],
    {
      element: <ManageLaborRates />,
      handle: { title: PAGE_TITLES.LABOR_RATES },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_DISPATCH][CONFIG_PAGES.ROLES_HR],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.ROLES_HR],
    ROLES_ELEMENTS({
      hidePermissions: true,
      modulePermission: PERMISSIONS.MI_DISPATCH.ACCESS,
      module: PlatformModules.MiDispatch,
    })
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_DISPATCH][
      CONFIG_PAGES.CANNOT_PERFORM_REASONS
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.CANNOT_PERFORM_REASONS],
    {
      element: <ManageCannotPerformReasons />,
      handle: { title: PAGE_TITLES.CANNOT_PERFORM_REASONS },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_DISPATCH][
      CONFIG_PAGES.MIDAY_SETTINGS
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.MIDAY_SETTINGS],
    {
      element: <ManageMiDaySettings />,
      handle: { title: PAGE_TITLES.MIDAY_SETTINGS },
    }
  ),
];

const miScheduledRoutes: WebPageRouteExtended[] = [
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_SCHEDULING][CONFIG_PAGES.USERS],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.USERS],
    {
      element: <ConfigUserList module={PERMISSIONS.MI_SCHEDULING.ACCESS} />,
      handle: { title: PAGE_TITLES.USERS },
    }
  ),
  getChildRouteData(
    `${
      MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_SCHEDULING][CONFIG_PAGES.USERS]
    }/:id`,
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_SCHEDULING][CONFIG_PAGES.USERS],
    {
      element: <ConfigUserDetails />,
      handle: { title: PAGE_TITLES.USER },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_SCHEDULING][
      CONFIG_PAGES.ORDER_CALENDARS
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.ORDER_CALENDARS],
    {
      element: <Outlet />,
      handle: { title: PAGE_TITLES.CALENDARS },
      childrenRoutes: [
        {
          index: true,
          element: (
            <ConfigCalendarSetList
              module={PlatformModules.MiScheduling}
              eventType={CalendarEventType.Order}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
        {
          path: ":id",
          element: (
            <ConfigCalendarSetDetails
              module={PlatformModules.MiScheduling}
              eventType={CalendarEventType.Order}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
        {
          path: "add-calendar",
          element: (
            <ConfigCalendarSetDetails
              module={PlatformModules.MiScheduling}
              eventType={CalendarEventType.Order}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
      ],
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_SCHEDULING][
      CONFIG_PAGES.WORK_ORDER_CALENDARS
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.WORK_ORDER_CALENDARS],
    {
      element: <Outlet />,
      featureFlagKey: FeatureFlagKey.AdvancedWorkOrdersEnabled,
      handle: { title: PAGE_TITLES.CALENDARS },
      childrenRoutes: [
        {
          index: true,
          element: (
            <ConfigCalendarSetList
              module={PlatformModules.MiScheduling}
              eventType={CalendarEventType.WorkOrder}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
        {
          path: ":id",
          element: (
            <ConfigCalendarSetDetails
              module={PlatformModules.MiScheduling}
              eventType={CalendarEventType.WorkOrder}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
        {
          path: "add-calendar",
          element: (
            <ConfigCalendarSetDetails
              module={PlatformModules.MiScheduling}
              eventType={CalendarEventType.WorkOrder}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
      ],
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_SCHEDULING][
      CONFIG_PAGES.WORK_ORDER_TEMPLATES
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.WORK_ORDER_TEMPLATES],
    {
      element: <Outlet />,
      childrenRoutes: [
        {
          index: true,
          element: (
            <ConfigWorkOrderTemplatesList
              module={PlatformModules.MiScheduling}
            />
          ),
          handle: { title: PAGE_TITLES.WORK_ORDER_TEMPLATES },
        },
        {
          path: ":id",
          element: (
            <ConfigWorkOrderTemplateDetails
              module={PlatformModules.MiScheduling}
            />
          ),
          handle: { title: PAGE_TITLES.WORK_ORDER_TEMPLATE },
        },
        {
          path: "add-work-order-type",
          element: (
            <ConfigWorkOrderTemplateDetails
              module={PlatformModules.MiScheduling}
            />
          ),
          handle: { title: PAGE_TITLES.ADD_WORK_ORDER_TEMPLATE },
        },
      ],
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_SCHEDULING][
      CONFIG_PAGES.WORK_ORDER_STEPS
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.WORK_ORDER_STEPS],
    {
      element: <Outlet />,
      childrenRoutes: [
        {
          index: true,
          element: (
            <ConfigWorkOrderStepsList module={PlatformModules.MiScheduling} />
          ),
          handle: { title: PAGE_TITLES.WORK_ORDER_STEPS },
        },
        {
          path: "add-work-order-step/:workOrderId",
          element: (
            <ConfigWorkOrderStepDetails module={PlatformModules.MiScheduling} />
          ),
          handle: { title: PAGE_TITLES.ADD_WORK_ORDER_STEP },
        },
        {
          path: ":workOrderId/:stepId",
          element: (
            <ConfigWorkOrderStepDetails module={PlatformModules.MiScheduling} />
          ),
          handle: { title: PAGE_TITLES.WORK_ORDER_STEP },
        },
      ],
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_SCHEDULING][
      CONFIG_PAGES.CALENDARS
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.CALENDARS],
    {
      element: <ConfigPageLayout />,
      handle: { title: PAGE_TITLES.CALENDARS },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_SCHEDULING][CONFIG_PAGES.ROLES_HR],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.ROLES_HR],
    ROLES_ELEMENTS({
      hidePermissions: true,
      modulePermission: PERMISSIONS.MI_SCHEDULING.ACCESS,
      module: PlatformModules.MiScheduling,
    })
  ),
];

const miAccountingRoutes: WebPageRouteExtended[] = [
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ACCOUNTING][CONFIG_PAGES.USERS],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.USERS],
    {
      element: <ConfigUserList module={PERMISSIONS.MI_ACCOUNTING.ACCESS} />,
      handle: { title: PAGE_TITLES.USERS },
    }
  ),
  getChildRouteData(
    `${
      MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ACCOUNTING][CONFIG_PAGES.USERS]
    }/:id`,
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ACCOUNTING][CONFIG_PAGES.USERS],
    {
      element: <ConfigUserDetails />,
      handle: { title: PAGE_TITLES.USER },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ACCOUNTING][
      CONFIG_PAGES.WORK_ORDER_TEMPLATES
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.WORK_ORDER_TEMPLATES],
    {
      element: <Outlet />,
      childrenRoutes: [
        {
          index: true,
          element: (
            <ConfigWorkOrderTemplatesList
              module={PlatformModules.MiAccounting}
            />
          ),
          handle: { title: PAGE_TITLES.WORK_ORDER_TEMPLATES },
        },
        {
          path: ":id",
          element: (
            <ConfigWorkOrderTemplateDetails
              module={PlatformModules.MiAccounting}
            />
          ),
          handle: { title: PAGE_TITLES.WORK_ORDER_TEMPLATE },
        },
        {
          path: "add-work-order-type",
          element: (
            <ConfigWorkOrderTemplateDetails
              module={PlatformModules.MiAccounting}
            />
          ),
          handle: { title: PAGE_TITLES.ADD_WORK_ORDER_TEMPLATE },
        },
      ],
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ACCOUNTING][
      CONFIG_PAGES.WORK_ORDER_STEPS
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.WORK_ORDER_STEPS],
    {
      element: <Outlet />,
      childrenRoutes: [
        {
          index: true,
          element: (
            <ConfigWorkOrderStepsList module={PlatformModules.MiAccounting} />
          ),
          handle: { title: PAGE_TITLES.WORK_ORDER_STEPS },
        },
        {
          path: "add-work-order-step/:workOrderId",
          element: (
            <ConfigWorkOrderStepDetails module={PlatformModules.MiAccounting} />
          ),
          handle: { title: PAGE_TITLES.ADD_WORK_ORDER_STEP },
        },
        {
          path: ":workOrderId/:stepId",
          element: (
            <ConfigWorkOrderStepDetails module={PlatformModules.MiAccounting} />
          ),
          handle: { title: PAGE_TITLES.WORK_ORDER_STEP },
        },
      ],
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ACCOUNTING][
      CONFIG_PAGES.QUICKBOOKS_INTEGRATION
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.QUICKBOOKS_INTEGRATION],
    {
      element: <ConfigQuickBooksIntegration />,
      handle: { title: PAGE_TITLES.QB_INTEGRATION },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ACCOUNTING][
      CONFIG_PAGES.QUICKBOOKS_IMPORT_CUSTOMERS
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.QUICKBOOKS_IMPORT_CUSTOMERS],
    {
      element: <ConfigQuickBooksImport />,
      handle: { title: PAGE_TITLES.QB_IMPORT },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ACCOUNTING][CONFIG_PAGES.ROLES_HR],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.ROLES_HR],
    ROLES_ELEMENTS({
      hidePermissions: true,
      modulePermission: PERMISSIONS.MI_ACCOUNTING.ACCESS,
    })
  ),
];

const miInspectionRoutes: WebPageRouteExtended[] = [
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_INSPECTIONS][CONFIG_PAGES.USERS],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.USERS],
    {
      element: <ConfigUserList module={PERMISSIONS.MI_INSPECTIONS.ACCESS} />,
      handle: { title: PAGE_TITLES.USERS },
    }
  ),
  getChildRouteData(
    `${
      MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_INSPECTIONS][CONFIG_PAGES.USERS]
    }/:id`,
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_INSPECTIONS][CONFIG_PAGES.USERS],
    {
      element: <ConfigUserDetails />,
      handle: { title: PAGE_TITLES.USER },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_INSPECTIONS][
      CONFIG_PAGES.INSPECTION_CALENDARS
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.INSPECTION_CALENDARS],
    {
      element: <Outlet />,
      handle: { title: PAGE_TITLES.CALENDARS },
      childrenRoutes: [
        {
          index: true,
          element: (
            <ConfigCalendarSetList
              module={PlatformModules.MiInspections}
              eventType={CalendarEventType.Inspection}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
        {
          path: ":id",
          element: (
            <ConfigCalendarSetDetails
              module={PlatformModules.MiInspections}
              eventType={CalendarEventType.Inspection}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
        {
          path: "add-calendar",
          element: (
            <ConfigCalendarSetDetails
              module={PlatformModules.MiInspections}
              eventType={CalendarEventType.Inspection}
            />
          ),
          handle: { title: PAGE_TITLES.CALENDARS },
        },
      ],
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_INSPECTIONS][
      CONFIG_PAGES.ROLES_HR
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.CALENDARS],
    {
      element: <ConfigPageLayout />,
      handle: { title: PAGE_TITLES.CALENDARS },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_INSPECTIONS][
      CONFIG_PAGES.ROLES_HR
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.ROLES_HR],
    ROLES_ELEMENTS({
      hidePermissions: true,
      modulePermission: PERMISSIONS.MI_INSPECTIONS.ACCESS,
      module: PlatformModules.MiInspections,
    })
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_INSPECTIONS][
      CONFIG_PAGES.INSPECTION_TEMPLATES
    ],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.INSPECTION_TEMPLATES],
    {
      element: <Outlet />,
      childrenRoutes: [
        {
          index: true,
          element: <ConfigInspectionTemplateList />,
          handle: { title: PAGE_TITLES.INSPECTION_TEMPLATES },
        },
        {
          path: ":id",
          element: <ConfigInspectionTemplateDetails />,
          handle: { title: PAGE_TITLES.INSPECTION_TEMPLATE },
        },
        {
          path: "add-inspection-type",
          element: <ConfigInspectionTemplateDetails />,
          handle: { title: PAGE_TITLES.ADD_INSPECTION_TEMPLATE },
        },
      ],
    }
  ),
];

const miEstimatingRoutes: WebPageRouteExtended[] = [
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ESTIMATING][CONFIG_PAGES.USERS],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.USERS],
    {
      element: <ConfigUserList module={PERMISSIONS.MI_ESTIMATING.ACCESS} />,
      handle: { title: PAGE_TITLES.USERS },
    }
  ),
  getChildRouteData(
    `${
      MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ESTIMATING][CONFIG_PAGES.USERS]
    }/:id`,
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ESTIMATING][CONFIG_PAGES.USERS],
    {
      element: <ConfigUserDetails />,
      handle: { title: PAGE_TITLES.USER },
    }
  ),
  getChildRouteData(
    MODULES_CONFIG_PATHS[PLATFORM_MODULES.MI_ESTIMATING][CONFIG_PAGES.ROLES_HR],
    CONFIG_ROUTE_NAMES[CONFIG_PAGES.ROLES_HR],
    ROLES_ELEMENTS({
      hidePermissions: true,
      modulePermission: PERMISSIONS.MI_ESTIMATING.ACCESS,
    })
  ),
];

export const configRoutes: WebPageRouteExtended[] = [
  getRouteData(CONFIG_PAGES.HOME),
  getRouteData(CONFIG_PAGES.MI_SCHEDULING),
  getRouteData(CONFIG_PAGES.MI_INSPECTIONS),
  getRouteData(CONFIG_PAGES.MI_ACCOUNTING),
  getRouteData(CONFIG_PAGES.MI_ESTIMATING),
  getRouteData(CONFIG_PAGES.MI_DISPATCH),
  getRouteData(CONFIG_PAGES.OPERATIONS),
  getRouteData(CONFIG_PAGES.SETTINGS, {
    element: <ConfigPageLayout />,
    handle: { title: PAGE_TITLES.SETTINGS },
  }),
  ...miDispatchRoutes,
  ...miScheduledRoutes,
  ...miInspectionRoutes,
  ...miAccountingRoutes,
  ...miEstimatingRoutes,
  ...hrConfigChildRoutes,
  ...operationsChildRoutes,
  ...autoChildRoutes,
];
